import React from 'react';
import { maskUsPhone } from 'utilities/formatPhone';
import '../../../styles/mastercertificate.css';
import '../../../styles/faxCoverPage.css';
import FarmersLogo from 'images/Farmers_Logo_png1.png';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';

export interface CoverPageData {
  companyName: string;
  address: string;
  phone: string;
  fax: string;
  to: string;
  toFaxNumber: string;
  date: string;
  from: string;
  fromFaxNumber: string;
  memo: string;
}

export function buildCoverPageXml(data: CoverPageData): string {
  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  return (
    `<?xml version="1.0" encoding="utf-16"?><CoverPage>` +
    `<CompanyName>${esc(data.companyName)}</CompanyName>` +
    `<Address>${esc(data.address)}</Address>` +
    `<Phone>${esc(data.phone)}</Phone>` +
    `<Fax>${esc(data.fax)}</Fax>` +
    `<To>${esc(data.to)}</To>` +
    `<ToFaxNumber>${esc(data.toFaxNumber)}</ToFaxNumber>` +
    `<Date>${esc(data.date)}</Date>` +
    `<From>${esc(data.from)}</From>` +
    `<FromFaxNumber>${esc(data.fromFaxNumber)}</FromFaxNumber>` +
    `<Memo>${esc(data.memo)}</Memo>` +
    `</CoverPage>`
  );
}

interface FaxCoverPageProps {
  visible: boolean;
  onHide: () => void;
  onSave: (data: CoverPageData, xml: string) => void;
  initialData?: Partial<CoverPageData>;
}

const FaxCoverPage: React.FC<FaxCoverPageProps> = ({ visible, onHide, onSave, initialData }) => {
  const scaleOptions = [
    { label: 'Fit', value: 'fit' },
    { label: '50%', value: 0.5 },
    { label: '100%', value: 1 },
    { label: '150%', value: 1.5 },
    { label: '200%', value: 2 },
  ];

  const [scale, setScale] = React.useState<any>(1);

  // Agency fields (readonly) — fetched once and cached
  const [agencyName, setAgencyName] = React.useState('');
  const [agencyFax, setAgencyFax] = React.useState('');
  const agencyCache = React.useRef<{ address: string; phone: string } | null>(null);

  // Editable fields
  const [address, setAddress] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [to, setTo] = React.useState(initialData?.to ?? '');
  const [toFaxNumber, setToFaxNumber] = React.useState(initialData?.toFaxNumber ?? '');
  const [memo, setMemo] = React.useState(initialData?.memo ?? '');

  // On every open: restore user-edited fields and prefill agency fields from cache or API
  React.useEffect(() => {
    if (!visible) return;

    // Restore user-edited fields from saved data
    setTo(initialData?.to ?? '');
    setToFaxNumber(initialData?.toFaxNumber ?? '');
    setMemo(initialData?.memo ?? '');

    const applyAgency = (agencyAddress: string, agencyPhone: string) => {
      setAddress(agencyAddress);
      setPhone(agencyPhone);
    };

    if (agencyCache.current) {
      applyAgency(agencyCache.current.address, agencyCache.current.phone);
      return;
    }

    const fetchAgency = async () => {
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/agency-details`, {
          headers: {
            Authorization: `Bearer ${igToken}`,
            NCToken: ncToken,
            Accept: 'application/json',
          },
        });
        if (!res.ok) return;
        const json = await res.json();
        const agency = json?.value?.[0];
        if (!agency) return;
        setAgencyName(agency.name ?? '');
        setAgencyFax(agency.fax ?? '');
        const mailingAddress = [agency.mailingAddress1, agency.mailingAddress2, agency.mailingAddressCity, agency.mailingAddressState, agency.mailingAddressZip].filter(Boolean).join(', ');
        agencyCache.current = { address: mailingAddress, phone: agency.phone ?? '' };
        applyAgency(mailingAddress, agency.phone ?? '');
      } catch {
        // non-critical
      }
    };
    fetchAgency();
  }, [visible]);

  const todayStr = React.useMemo(() => {
    const d = new Date();
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  }, []);

  const handleSave = () => {
    const data: CoverPageData = {
      companyName: agencyName,
      address,
      phone,
      fax: agencyFax,
      to,
      toFaxNumber,
      date: todayStr,
      from: agencyName,
      fromFaxNumber: agencyFax,
      memo,
    };
    onSave(data, buildCoverPageXml(data));
    onHide();
  };

  if (!visible) return null;

  return (
    <div className="masterCert__popupOverlay">
      <div className="masterCert__popup" style={{ maxWidth: 900, height: 700, display: 'flex', flexDirection: 'column' }}>
        <div className="masterCert__popupHeader" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Fax Cover Page</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {scaleOptions.map(opt => (
              <Button
                key={opt.label}
                type="button"
                label={opt.label}
                className={scale === opt.value ? 'p-button-primary carrierDialog__saveBtn' : 'p-button-text carrierDialog__cancelBtn'}
                style={{ minWidth: 48, fontSize: 13, padding: '0 8px', fontWeight: 600 }}
                onClick={() => setScale(opt.value)}
              />
            ))}
          </div>
          <button className="masterCert__popupCloseBtn" onClick={onHide}>×</button>
        </div>
        <div style={{ flex: 1, width: '100%', height: 0, minHeight: 0, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
          <div
            className="faxCoverPage__container"
            style={{
              maxWidth: 800,
              minWidth: 600,
              margin: '0 auto',
              paddingBottom: 0,
              transform: scale === 'fit' ? 'scale(1)' : `scale(${scale})`,
              transformOrigin: 'top left',
              transition: 'transform 0.2s',
              width: scale === 'fit' ? '100%' : 800,
            }}
          >
            <div className="faxCoverPage__header">
              <img src={FarmersLogo} alt="Farmers Logo" className="faxCoverPage__logo" />
              <div className="faxCoverPage__title">Fax Cover Page</div>
            </div>
            <div className="faxCoverPage__divider" />

            {/* Section 1: Company info — stacked rows */}
            <div className="carrierDialog__card">
              <div className="faxCoverPage__row">
                <span className="faxCoverPage__rowLabel">Company name:</span>
                <InputText className="carrierDialog__inputReadonly faxCoverPage__rowInput" value={agencyName} readOnly />
              </div>
              <div className="faxCoverPage__row">
                <span className="faxCoverPage__rowLabel">Mailing address:</span>
                <InputText maxLength={100} className="carrierDialog__input faxCoverPage__rowInput" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter mailing address" />
              </div>
              <div className="faxCoverPage__row">
                <span className="faxCoverPage__rowLabel">Phone:</span>
                <InputText maxLength={20} className="carrierDialog__input faxCoverPage__rowInput" value={phone} onChange={e => setPhone(maskUsPhone(e.target.value))} placeholder="Enter phone number" />
              </div>
              <div className="faxCoverPage__row">
                <span className="faxCoverPage__rowLabel">Fax:</span>
                <InputText className="carrierDialog__inputReadonly faxCoverPage__rowInput" value={agencyFax} readOnly />
              </div>
            </div>

            {/* Section 2: TO / FROM grid */}
            <div className="carrierDialog__card">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px' }}>
                {/* Row 1 */}
                <div className="faxCoverPage__row">
                  <span className="faxCoverPage__rowLabel" style={{ minWidth: 60 }}>TO:</span>
                  <InputText maxLength={100} className="carrierDialog__input faxCoverPage__rowInput" value={to} onChange={e => setTo(e.target.value)} placeholder="Recipient name" />
                </div>
                <div className="faxCoverPage__row">
                  <span className="faxCoverPage__rowLabel" style={{ minWidth: 60 }}>FROM:</span>
                  <InputText className="carrierDialog__inputReadonly faxCoverPage__rowInput" value={agencyName} readOnly />
                </div>
                {/* Row 2 */}
                <div className="faxCoverPage__row">
                  <span className="faxCoverPage__rowLabel" style={{ minWidth: 60 }}>FAX #:</span>
                  <InputText maxLength={20} className="carrierDialog__input faxCoverPage__rowInput" value={toFaxNumber} onChange={e => setToFaxNumber(maskUsPhone(e.target.value))} placeholder="Recipient fax" />
                </div>
                <div className="faxCoverPage__row">
                  <span className="faxCoverPage__rowLabel" style={{ minWidth: 60 }}>FAX #:</span>
                  <InputText className="carrierDialog__inputReadonly faxCoverPage__rowInput" value={agencyFax} readOnly />
                </div>
                {/* Row 3 */}
                <div className="faxCoverPage__row">
                  <span className="faxCoverPage__rowLabel" style={{ minWidth: 60 }}>DATE:</span>
                  <InputText className="carrierDialog__inputReadonly faxCoverPage__rowInput" value={todayStr} readOnly />
                </div>
                <div />
              </div>
            </div>

            {/* Section 3: Memo */}
            <div className="carrierDialog__card">
              <div className="carrierDialog__label">Memo</div>
              <InputTextarea maxLength={500}
                className="carrierDialog__textarea"
                style={{ minHeight: 120, width: '100%', resize: 'vertical', maxHeight: 300 }}
                value={memo}
                onChange={e => setMemo(e.target.value)}
                placeholder="Enter memo details"
              />
            </div>
          </div>
        </div>
        <div className="faxCoverPage__footer">
          <Button type="button" label="Cancel" className="p-button-text carrierDialog__cancelBtn" onClick={onHide} />
          <Button type="button" label="Save Cover Page" className="p-button-primary carrierDialog__saveBtn" onClick={handleSave} />
        </div>
      </div>
    </div>
  );
};
export default FaxCoverPage;
